import {
  DatasetRoleID,
  DatasetUserResponse,
  DatasetUserRoleReference,
  LabelResponse,
  ProjectRoleID,
  ProjectUserResponse,
  ProjectUserRoleReference,
} from '@backend';
import {DatasetRole, DatasetRoleOptions} from '@shared/labels/dataset-role';
import {ProjectRole, ProjectRoleOptions} from '@shared/labels/project-role';

export interface PermissionRow {
  email: string;
  roleId: string;
  roleLabel: string;
  isProjectPi?: boolean | null;
}

export type PermissionsContext = {
  type: 'project' | 'dataset';
  id: string;
  editable: boolean;
  showEditButton: boolean;
  syncFromPrimaryProject?: () => boolean;
  canEnableOrganizationalAccess?: boolean;
  organizationalAccess?: () => boolean;
  orgName: string;
};

export function mapUsersToPermissionRows(
  users: ProjectUserResponse[] | undefined,
  context: PermissionsContext,
  labels: typeof ProjectRoleOptions,
): PermissionRow[];
export function mapUsersToPermissionRows(
  users: DatasetUserResponse[] | undefined,
  context: PermissionsContext,
  labels: typeof DatasetRoleOptions,
): PermissionRow[];
export function mapUsersToPermissionRows(
  users: (ProjectUserResponse | DatasetUserResponse)[] | null | undefined,
  context: PermissionsContext,
  labelMap: Record<ProjectRole, LabelResponse> | Record<DatasetRole, LabelResponse>,
): PermissionRow[] {
  if (!users?.length) return [];

  if (context.type === 'project') {
    return (users as ProjectUserResponse[]).map(user => ({
      email: user.user.email,
      roleId: user.project_role_id,
      roleLabel: labelMap[user.project_role_id]?.text ?? user.project_role_id,
      isProjectPi: user.is_project_pi ?? false,
    }));
  }

  return (users as DatasetUserResponse[]).map(user => ({
    email: user.user.email,
    roleId: user.dataset_role_id,
    roleLabel: labelMap[user.dataset_role_id]?.text ?? user.dataset_role_id,
  }));
}

//Map UI -> Patch
export function mapPermissionRowToRoleReference(
  context: PermissionsContext,
  row: PermissionRow,
): ProjectUserRoleReference | DatasetUserRoleReference {
  if (context.type === 'project') {
    return {
      email: row.email,
      role: row.roleId as ProjectRoleID,
      is_project_pi: row.isProjectPi ?? false,
    };
  }

  return {
    email: row.email,
    role: row.roleId as DatasetRoleID,
  };
}

// Patch
export function buildPatchPayload(
  context: PermissionsContext,
  rows: PermissionRow[],
): ProjectUserRoleReference[] | DatasetUserRoleReference[] {
  if (context.type === 'project') {
    return rows.map(row => ({
      email: row.email,
      role: row.roleId as ProjectRoleID,
      is_project_pi: row.isProjectPi ?? false,
    }));
  }

  return rows.map(row => ({
    email: row.email,
    role: row.roleId as DatasetRoleID,
  }));
}

// UI Helpers
export function updatePermissionRow(rows: PermissionRow[], updatedRow: PermissionRow): PermissionRow[] {
  return rows.map(row => (row.email === updatedRow.email ? updatedRow : row));
}

export function removePermissionRow(rows: PermissionRow[], email: string): PermissionRow[] {
  return rows.filter(row => row.email !== email);
}

export function addPermissionRow(rows: PermissionRow[], newRow: PermissionRow): PermissionRow[] {
  if (rows.some(row => row.email === newRow.email)) {
    return rows;
  }
  return [...rows, newRow];
}
